import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * Inline error banner for list pages.
 *
 * Renders above the list when the most recent fetch failed but the user
 * still has a session (no auth wipe). One-click Retry to refetch.
 *
 * Props:
 *   error:    Error | null   — the most recent fetch error (null hides the banner)
 *   onRetry:  () => void     — re-runs the fetch
 *   busy:     boolean        — disables the Retry button while a retry is in flight
 *   label:    string         — what we were trying to load (e.g. "GRN records")
 */
export default function ListLoadError({ error, onRetry, busy, label = 'data' }) {
  if (!error) return null;

  // Compose a human-readable hint based on the error shape
  const status = error?.response?.status;
  let hint;
  if (status === 524 || status === 504 || status === 408) {
    hint = 'The request timed out — the list may be very large.';
  } else if (status >= 500) {
    hint = `Server returned ${status}. This is usually temporary.`;
  } else if (status === 0 || error?.message === 'Network Error' || !error?.response) {
    hint = 'No response from the server. Check your connection.';
  } else if (status === 403) {
    hint = "You don't have permission to view this list.";
  } else {
    hint = error?.response?.data?.detail || error?.message || 'Unknown error.';
  }

  return (
    <div
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-3 flex flex-col sm:flex-row sm:items-center gap-3"
      data-testid="list-load-error-banner"
      role="alert"
    >
      <div className="flex items-start gap-2 flex-1 min-w-0">
        <AlertTriangle className="text-amber-600 flex-shrink-0 mt-0.5" size={18} />
        <div className="text-sm text-amber-900 min-w-0">
          <div className="font-semibold">Couldn't load {label}</div>
          <div className="text-xs text-amber-800 truncate" title={hint}>{hint}</div>
        </div>
      </div>
      <button
        onClick={onRetry}
        disabled={busy}
        className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-amber-600 text-white rounded-md text-sm font-medium hover:bg-amber-700 disabled:opacity-60 disabled:cursor-not-allowed"
        data-testid="list-load-retry-btn"
      >
        <RefreshCw size={14} className={busy ? 'animate-spin' : ''} />
        {busy ? 'Retrying…' : 'Retry'}
      </button>
    </div>
  );
}
